/**
 * The messages between the page and the find worker (decision 41; part 19a log, tables T and RP; part 19b, tables B and
 * D). The page sends a search, a replace or a count with the chapters' texts; the worker answers with ticks while it
 * works (M7), then one answer. Each
 * side reads what it receives field by field (Definition of Done, security; T4), so a message that isn't one of these is
 * never taken for one.
 */
import { MAX_QUERY_LENGTH } from '@cockatrice/core';
import type { FindQuery, QueryProblem } from '@cockatrice/core';

/** A chapter to search: its ID and its text (the open chapter's as typed, the others' as stored: M5). */
export interface FindChapter {
  readonly id: string;
  readonly text: string;
}

/** A search: every match's count per chapter, and the matches' source ranges in chapter `ranges` (M4). */
export interface SearchJob {
  readonly kind: 'search';
  readonly id: number;
  readonly query: FindQuery;
  readonly chapters: readonly FindChapter[];
  /** The chapter whose ranges come back (the open one), or null for counts alone. */
  readonly ranges: string | null;
}

/** F5: the one match a single replace makes: its chapter and source range. */
export interface OnlyMatch {
  readonly chapter: string;
  readonly from: number;
  readonly to: number;
}

/** F5, F6: a replace of every match, or of `only`, with `replacement` (a template when the query is a regex: RP4). */
export interface ReplaceJob {
  readonly kind: 'replace';
  readonly id: number;
  readonly query: FindQuery;
  readonly replacement: string;
  readonly chapters: readonly FindChapter[];
  readonly only: OnlyMatch | null;
}

/** Part 19b, B1: a range of one of the chapters sent (the open chapter's selection). */
export type ChapterRange = OnlyMatch;

/** Part 19b (tables B and D): the words of each chapter sent, and of `selection` in one of them (table S). */
export interface CountJob {
  readonly kind: 'count';
  readonly id: number;
  readonly chapters: readonly FindChapter[];
  readonly selection: ChapterRange | null;
}

/** What the page asks the worker. */
export type FindJob = SearchJob | ReplaceJob | CountJob;

/**
 * One chapter's replace: its new text (null when no match was made, so it is unchanged), its matches made and left, and
 * for a single replace made, where in the new text the replacement ends (deep-fix r3: the move goes on from there), or
 * null when that can't be found.
 */
export interface ReplacedChapter {
  readonly text: string | null;
  readonly made: number;
  readonly left: number;
  readonly after: number | null;
}

/** M7: the worker is still at job `id`. */
export interface FindTick {
  readonly kind: 'tick';
  readonly id: number;
}

/** A search's answer: a count per chapter sent, in order, and the ranges of the chapter asked for (`[from₀, to₀, …]`). */
export interface FoundAnswer {
  readonly kind: 'found';
  readonly id: number;
  readonly counts: readonly number[];
  readonly ranges: readonly number[];
}

/** A replace's answer: one entry per chapter sent, in order. */
export interface ReplacedAnswer {
  readonly kind: 'replaced';
  readonly id: number;
  readonly chapters: readonly ReplacedChapter[];
}

/** A count's answer: the words of each chapter sent, in order, and of the selection asked for (null with none). */
export interface CountedAnswer {
  readonly kind: 'counted';
  readonly id: number;
  readonly counts: readonly number[];
  readonly selection: number | null;
}

/** No pattern (table Q: Q3, Q7; the replacement too long), or the job failed in the worker (T4). */
export type FindRefusal = { readonly kind: 'refused'; readonly id: number; readonly problem: QueryProblem } | { readonly kind: 'failed'; readonly id: number };

/** Anything the worker sends. */
export type FindMessage = FindTick | FoundAnswer | ReplacedAnswer | CountedAnswer | FindRefusal;

const PROBLEMS: readonly QueryProblem[] = ['too-long', 'invalid'];

/** The own fields of a plain object with exactly `count` fields, or null (as the import worker's, part 17). */
function fieldsOf(value: unknown, count: number): ReadonlyMap<string, unknown> | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const fields = new Map(Object.entries(value));
  return fields.size === count ? fields : null;
}

/** Whether `value` is a whole number from 0. */
function isCount(value: unknown): value is number {
  return Number.isSafeInteger(value) && Number(value) >= 0;
}

/** A query: its text (at most Q7's length) and three booleans; null for anything else. */
function readQuery(value: unknown): FindQuery | null {
  const fields = fieldsOf(value, 4);
  const text = fields?.get('text');
  const matchCase = fields?.get('matchCase');
  const wholeWords = fields?.get('wholeWords');
  const regex = fields?.get('regex');
  if (typeof text !== 'string' || text.length > MAX_QUERY_LENGTH || typeof matchCase !== 'boolean' || typeof wholeWords !== 'boolean' || typeof regex !== 'boolean') return null;
  return { text, matchCase, wholeWords, regex };
}

/** The chapters of a job: each exactly an ID and a text, no ID twice; null for anything else. */
function readChapters(value: unknown): FindChapter[] | null {
  if (!Array.isArray(value)) return null;
  const out: FindChapter[] = [];
  const ids = new Set<string>();
  for (const item of value) {
    const fields = fieldsOf(item, 2);
    const id = fields?.get('id');
    const text = fields?.get('text');
    if (typeof id !== 'string' || typeof text !== 'string' || ids.has(id)) return null;
    ids.add(id);
    out.push({ id, text });
  }
  return out;
}

/** F5: the one match of a replace (part 19b: a count's selection), in one of `chapters`; null for anything else. */
function readOnly(value: unknown, chapters: readonly FindChapter[]): OnlyMatch | null {
  const fields = fieldsOf(value, 3);
  const chapter = fields?.get('chapter');
  const from = fields?.get('from');
  const to = fields?.get('to');
  if (typeof chapter !== 'string' || !isCount(from) || !isCount(to) || from >= to || !chapters.some((each) => each.id === chapter)) return null;
  return { chapter, from, to };
}

/** The job a message asks for, or null for anything else (T4). */
export function readJob(value: unknown): FindJob | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'search') {
    const fields = fieldsOf(value, 5);
    const id = fields?.get('id');
    const query = readQuery(fields?.get('query'));
    const chapters = readChapters(fields?.get('chapters'));
    const ranges = fields?.get('ranges');
    if (!isCount(id) || query === null || chapters === null) return null;
    const asked = chapters.find((chapter) => chapter.id === ranges);
    if (ranges !== null && asked === undefined) return null;
    return { kind, id, query, chapters, ranges: asked?.id ?? null };
  }
  if (kind === 'replace') {
    const fields = fieldsOf(value, 6);
    const id = fields?.get('id');
    const query = readQuery(fields?.get('query'));
    const replacement = fields?.get('replacement');
    const chapters = readChapters(fields?.get('chapters'));
    const only = fields?.get('only');
    if (!isCount(id) || query === null || typeof replacement !== 'string' || chapters === null) return null;
    if (only === null) return { kind, id, query, replacement, chapters, only };
    const one = readOnly(only, chapters);
    return one === null ? null : { kind, id, query, replacement, chapters, only: one };
  }
  if (kind === 'count') {
    const fields = fieldsOf(value, 4);
    const id = fields?.get('id');
    const chapters = readChapters(fields?.get('chapters'));
    const selection = fields?.get('selection');
    if (!isCount(id) || chapters === null) return null;
    if (selection === null) return { kind, id, chapters, selection };
    const range = readOnly(selection, chapters);
    return range === null ? null : { kind, id, chapters, selection: range };
  }
  return null;
}

/**
 * M4: ranges, a flat list of whole numbers in pairs, each pair non-empty and within `length`, neither its start nor its end
 * before the pair before's (deep-fix r2: every digit of a note number maps to the whole reference, so two matches in one
 * number share a span, and one ending in it overlaps the next; part 04's spans never go back).
 */
function readRanges(value: unknown, length: number): number[] | null {
  if (!Array.isArray(value) || value.length % 2 !== 0) return null;
  const out: number[] = [];
  for (let i = 0; i < value.length; i += 2) {
    const from: unknown = value[i];
    const to: unknown = value[i + 1];
    if (!isCount(from) || !isCount(to) || from >= to || to > length || from < (out.at(-2) ?? 0) || to < (out.at(-1) ?? 0)) return null;
    out.push(from, to);
  }
  return out;
}

/**
 * A replace's chapter: a text or null (null exactly when nothing was made), two counts, and a point after the replacement
 * inside the text, only for a single replace (`single`) that made one.
 */
function readReplaced(value: unknown, single: boolean): ReplacedChapter | null {
  const fields = fieldsOf(value, 4);
  const text = fields?.get('text');
  const made = fields?.get('made');
  const left = fields?.get('left');
  const after = fields?.get('after');
  if (!(text === null || typeof text === 'string') || !isCount(made) || !isCount(left) || (text === null) !== (made === 0)) return null;
  if (after !== null && (!single || text === null || !isCount(after) || after > text.length)) return null;
  return { text, made, left, after };
}

/**
 * The message the worker sent about `job`, or null for anything else (T4): an answer must be about that job and fit
 * it: a count for each chapter sent, ranges inside the chapter asked for, a replace's entry for each chapter and, for a
 * single replace, at most one match made.
 */
export function readMessage(value: unknown, job: FindJob): FindMessage | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  const id: unknown = kind === undefined ? undefined : Reflect.get(Object(value), 'id');
  if (id !== job.id) return null;
  if (kind === 'tick' || kind === 'failed') return fieldsOf(value, 2) === null ? null : { kind, id };
  if (kind === 'refused') {
    const problem = PROBLEMS.find((known) => known === fieldsOf(value, 3)?.get('problem'));
    return problem === undefined ? null : { kind, id, problem };
  }
  if (kind === 'found' && job.kind === 'search') {
    const fields = fieldsOf(value, 4);
    const counts = fields?.get('counts');
    if (!Array.isArray(counts) || counts.length !== job.chapters.length || !counts.every(isCount)) return null;
    const asked = job.chapters.find((chapter) => chapter.id === job.ranges);
    const ranges = readRanges(fields?.get('ranges'), asked?.text.length ?? 0);
    const index = job.chapters.findIndex((chapter) => chapter.id === job.ranges);
    if (ranges === null || (index < 0 ? ranges.length > 0 : ranges.length !== 2 * Number(counts[index]))) return null;
    return { kind, id, counts, ranges };
  }
  if (kind === 'replaced' && job.kind === 'replace') {
    const list = fieldsOf(value, 3)?.get('chapters');
    if (!Array.isArray(list) || list.length !== job.chapters.length) return null;
    const chapters: ReplacedChapter[] = [];
    for (const item of list) {
      const one = readReplaced(item, job.only !== null);
      if (one === null) return null;
      chapters.push(one);
    }
    if (job.only !== null && chapters.reduce((sum, chapter) => sum + chapter.made + chapter.left, 0) > 1) return null;
    return { kind, id, chapters };
  }
  if (kind === 'counted' && job.kind === 'count') {
    // Part 19b: a count for each chapter sent, and a selection's exactly when one was asked, never more than its
    // chapter's (S6).
    const fields = fieldsOf(value, 4);
    const counts = fields?.get('counts');
    const selection = fields?.get('selection');
    if (!Array.isArray(counts) || counts.length !== job.chapters.length || !counts.every(isCount)) return null;
    const asked = job.selection;
    if (asked === null) return selection === null ? { kind, id, counts, selection } : null;
    const of = Number(counts[job.chapters.findIndex((chapter) => chapter.id === asked.chapter)]);
    return isCount(selection) && selection <= of ? { kind, id, counts, selection } : null;
  }
  return null;
}
